import React from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { FormField } from '../../../global/form/form-field/form-field';
import { CommonPopupBodyFooterLayout } from '../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../global/sub-window/common/common-close-sub-window-buttons';
import { Permission, RolesAction } from '../../../../api/roles.dao';
import { MultiSelectButton } from '../../../global/multi-select/multi-select-button';
import Text from '../../../global/text/text';
import DefaultButton from '../../../global/default-button/default-button';
import { AccountGroupsFormVM } from '../account-groups-form.viewmodel';
import OptionsButton from '../../../global/options-button/options-button';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { ADD_TEXT, addText, APPLY_TEXT, CANCEL_TEXT, cancelText, DESCRIPTION, REMOVE_TEXT } from '../../../../constants/common-labels.constants';

interface IProps {
    vm: AccountGroupsFormVM;
    intl: IntlShape;
}

const AccountGroupsFormPresentation = injectIntl(
    mobxReact.observer((props: IProps) => {
        const {
            intl,
            vm,
            vm: { groupFormDataVM, accountGroupId, isSaving, isLoading, handleSubmit, onDeleteGroup }
        } = props;

        const isEdit = !!accountGroupId;

        const formSubmitHandler = () => {
           const defineFormType = isEdit ? RolesAction.Edit : RolesAction.Create;
           return handleSubmit(intl, defineFormType);
        };

        const groupOptions = [
            isEdit && hasPermission(Permission.DELETE_ACCOUNT_GROUP_LIST) && {
                label: intl.formatMessage({
                    id: 'oe.accountGroup.deleteAccountGroup',
                    description: 'label text',
                    defaultMessage: 'Delete Account Group'
                }),
                action: onDeleteGroup,
                danger: true
            }
        ].filter(Boolean);

        if (isLoading) {
            return (
                <CommonPopupBodyFooterLayout
                    body={
                        <div className='oe-form meeting-form'>
                            <FormattedMessage
                                description='label text'
                                defaultMessage='Loading group data'
                                id='oe.accountGroups.loadingData'
                            />
                        </div>
                    }
                />
            );
        }

        return (
            <CommonPopupBodyFooterLayout
                body={
                    <div>
                        <div className='oe-form'>
                            {
                                <div className='oe-form__body'>
                                    <FormField
                                        label={intl.formatMessage({
                                            id: 'oe.accountGroup.name',
                                            description: 'Account group name title',
                                            defaultMessage: 'Name'
                                        })}
                                    >
                                        <input
                                            type='text'
                                            className='oe-form-field__control'
                                            value={groupFormDataVM.name}
                                            onChange={({ target: { value } }) => groupFormDataVM.setName(value)}
                                        />
                                        {groupFormDataVM.showValidationErrors() ? (
                                            <div className='oe-form-field__error'>
                                                {groupFormDataVM.getNameError(intl)}
                                            </div>
                                        ) : null}
                                    </FormField>
                                    <FormField
                                        label={intl.formatMessage(DESCRIPTION)}
                                    >
                                        <textarea
                                            className='oe-form-field__control'
                                            value={groupFormDataVM.description}
                                            onChange={({ target: { value } }) => groupFormDataVM.setDescription(value)}
                                        />
                                        {groupFormDataVM.showValidationErrors() ? (
                                            <div className='oe-form-field__error'>
                                                {groupFormDataVM.getDescriptionError(intl)}
                                            </div>
                                        ) : null}
                                    </FormField>
                                    {groupFormDataVM.serverErrorMessage && (
                                        <div className='oe-form-field__error'>{groupFormDataVM.serverErrorMessage}</div>
                                    )}

                                    <div className='event_form__items-section'>
                                        <div className='event_form__items-section__title'>
                                            <FormattedMessage
                                                id='oe.common.accounts'
                                                description='Label text'
                                                defaultMessage='Accounts'
                                            />
                                        </div>
                                        <div className='event_form__items-section__selected-items'>
                                            {vm.groupFormDataVM.accounts
                                                .slice()
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                .map(({ accountId, name }) => (
                                                    <div
                                                        key={accountId}
                                                        className='event_form__items-section__item-wrapper'
                                                    >
                                                        <Text
                                                            className='event_form__items-section__item-label'
                                                            message={name}
                                                            withEllipsis
                                                        />
                                                        <DefaultButton
                                                            text={<FormattedMessage {...REMOVE_TEXT} />}
                                                            modifier='remove'
                                                            onClick={() => groupFormDataVM.removeAccount(accountId)}
                                                            className='event_form__items-section__remove-item-button'
                                                        />
                                                    </div>
                                                ))}
                                        </div>
                                        <MultiSelectButton
                                            className='event_form__items-section__add-button'
                                            items={vm.accountsList}
                                            disabled={[]}
                                            value={groupFormDataVM.accounts || []}
                                            keyProperty='accountId'
                                            nameProperty='name'
                                            text={
                                                <FormattedMessage
                                                    id='oe.accounts.add'
                                                    description='Button text'
                                                    defaultMessage='{prefix}Add Account'
                                                    values={{
                                                        prefix: '+ '
                                                    }}
                                                />
                                            }
                                            applyButtonText={addText()}
                                            resetButtonText={cancelText()}
                                            onChange={groupFormDataVM.setAccounts}
                                            filterHandler={(query: string) => vm.fetchAccountsList(query)}
                                            searchWithDebounce
                                        />
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                }
                footer={
                    <>
                        {isSaving && (
                            <span className='oe-popup-layout__footer-status-area'>
                                {intl.formatMessage({
                                    id: 'oe.accountGroups.groupIsBeingSaved',
                                    description: 'placeholder about saving',
                                    defaultMessage: 'Group is being saved, please wait...'
                                })}
                            </span>
                        )}

                        <div className='buttons-block buttons-block--from-right'>
                            <DefaultCloseSubWindowButton
                                text={intl.formatMessage(isEdit ? APPLY_TEXT : ADD_TEXT)}
                                modifier={['fat', 'confirm-safe']}
                                onClick={formSubmitHandler}
                                disabled={isSaving}
                            />
                            <DefaultCloseSubWindowButton
                                text={intl.formatMessage(CANCEL_TEXT)}
                            />
                            {!!groupOptions.length && (
                                <OptionsButton
                                    className='options-button--right-margin-auto'
                                    modifier={['fat', 'options']}
                                    label='More...'
                                    options={groupOptions}
                                    position='top'
                                    align='start'
                                />
                            )}
                        </div>
                    </>
                }
            />
        );
    })
);

export { AccountGroupsFormPresentation };
